import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import BottomNavigation from '@mui/material/BottomNavigation';

import { handleNullValue } from 'bento-ordering/base/utils/stringHelpers';
import { CartBorder } from 'bento-ordering/base/components/CartBorder';
import CartItemsList from 'bento-ordering/cart/components/CartItemsList';
import CartFulfillmentMethod from 'bento-ordering/cart/components/CartFulfillmentMethod';
import { useLayout } from 'bento-ordering/context/LayoutContext';
import { isMobileMedia } from 'bento-ordering/utils/media';
import { getFormattedDollarDifference } from 'helpers/formattedDollar';
import CloseButton from 'bento-ordering/base/ui_components/CloseButton';
import CartHeader from 'components/Cart/CartHeader';
import MenuUpsell from 'bento-ordering/cart/components/MenuUpsell';
import OutlinedBoxError from 'components/OutlinedBoxError';
import VisuallyHiddenLink from 'components/VisuallyHiddenLink';
import CartPageCheckoutContainer from 'bento-ordering/cart/containers/CartPageCheckoutContainer';
import GroupOrderDinerFooter from 'bento-ordering/group_ordering/components/GroupOrderDinerFooter';
import { LEFT } from 'bento-ordering/base/constants/alignment';
import { PROMOTION_PROPTYPES } from 'propTypes';
import { styled } from '@mui/system';
import muiPropForwarding from 'styles/helpers/muiPropForwarding';
import { getPageBackgroundColor } from 'styles/helpers/getThemeStyles';
import {
  selectCartIsGroupOrder,
  selectCartGroupOrderSpendingLimit,
  selectCartGroupOrderDeadline,
  selectCartGroupOrderAdminName,
  selectIsGroupOrderCurrentDinerDoneOrdering
} from 'bento-ordering/cart/selectors/index';
import CartGroupOrderInfo from 'bento-ordering/cart/components/CartGroupOrderInfo';
import {
  useIsGroupOrderGuestDiner,
  useIsGroupOrderAdminDiner,
  useGroupOrderDinersCart
} from 'bento-ordering/group_ordering/hooks/index';
import GroupOrderAdminCartView from 'bento-ordering/group_ordering/components/GroupOrderAdminCartView';
import CartInfoManager from 'bento-ordering/cart/components/CartInfoManager';
import { useHasInvestorDayFeatureFlag } from 'bento-ordering/utils/featureFlags/hasInvestorDayFeatureFlag';

const StyledCart = styled(Box, {
  shouldForwardProp: muiPropForwarding('isMobile')
})(({ isMobile }) => ({
  paddingBottom: isMobile ? '26px' : '0px'
}));

const StyledUpsellContainer = styled('div', {
  shouldForwardProp: muiPropForwarding('isMobile')
})(({ isMobile }) => ({
  paddingBottom: !isMobile ? '120px' : '0'
}));

const CheckoutCtaAndErrorsWrapper = styled(BottomNavigation)(({ theme }) => ({
  left: 'auto',
  right: '0',
  flexDirection: 'column',
  backgroundColor: getPageBackgroundColor(theme),
  width: '25rem'
}));

function Cart({
  items,
  toggleIsOpen,
  getEditCartItemPath,
  removeItemFromCart,
  fulfillmentMethod,
  currentPrepTime,
  restaurantName,
  requested_fulfillment_is_asap,
  total_owed,
  tax,
  subtotal,
  fulfillment_date,
  fulfillment_time,
  handleClose,
  handleEditClick,
  currentCartPath,
  changeFulfillment,
  isTotalBelowMinimum,
  orderMinimum,
  online_ordering_service_fee: onlineOrderingServiceFee,
  taxes_and_fees,
  fulfillmentTimeOrASAP,
  shipping_street_address_1,
  deliveryFee,
  customFee,
  customFeeName,
  bundleServiceFeeWithCustomFee,
  bundledServiceAndCustomFees,
  cartValidationError,
  cartQuantityError,
  digitalGiftCardAmountApplied,
  covidFee,
  tipAmount,
  promotion,
  disableCheckoutButton = false
}) {
  const isGroupOrder = useSelector(selectCartIsGroupOrder);
  const isGroupOrderGuestDiner = useIsGroupOrderGuestDiner();
  const isGroupOrderAdminDiner = useIsGroupOrderAdminDiner();
  const groupOrderSpendingLimit = useSelector(
    selectCartGroupOrderSpendingLimit
  );
  const groupOrderDeadline = useSelector(selectCartGroupOrderDeadline);
  const groupOrderAdminName = useSelector(selectCartGroupOrderAdminName);
  const isDinerDoneOrdering = useSelector(
    selectIsGroupOrderCurrentDinerDoneOrdering
  );
  const hasInvestorDayFeature = useHasInvestorDayFeatureFlag();

  const hideUpsells =
    isGroupOrder && isGroupOrderGuestDiner && isDinerDoneOrdering;

  const {
    adminDiner,
    guestDiners,
    dinersCount,
    renderShowAllGuestsButton,
    hideShowAllGuestsButton
  } = useGroupOrderDinersCart();

  const showGroupOrderInfo =
    isGroupOrder && (groupOrderSpendingLimit || groupOrderDeadline);

  const handleClick = () => {
    handleClose();
  };

  const renderCartValidationErrors = () => (
    <Box data-testid="cart-item-inventory-error" mt={2.5}>
      <OutlinedBoxError>{cartValidationError}</OutlinedBoxError>
    </Box>
  );

  const renderCartQuantityUpdateErrors = () => (
    <Box data-testid="cart-item-quantity-error" mt={2.5}>
      <OutlinedBoxError>{cartQuantityError}</OutlinedBoxError>
    </Box>
  );

  const amountFromMinimum = getFormattedDollarDifference(
    orderMinimum,
    subtotal
  );

  const isMobile = isMobileMedia();

  const { bottomNavComponentRef } = useLayout();

  return (
    <>
      <StyledCart
        id="cart"
        data-testid="bento-ordering-cart"
        isMobile={isMobile}
      >
        <CloseButton autoFocus handleClick={handleClick} />

        <CartHeader
          fulfillmentMethod={fulfillmentMethod}
          onClick={changeFulfillment}
          isGroupOrder={isGroupOrder}
          groupOrderAdminName={groupOrderAdminName}
        />

        {!hasInvestorDayFeature && (
          <VisuallyHiddenLink href="#cart-checkout-button">
            Skip to checkout
          </VisuallyHiddenLink>
        )}

        {!hasInvestorDayFeature && (
          <CartFulfillmentMethod
            fulfillmentAddress={handleNullValue(shipping_street_address_1)}
            fulfillmentTimeOrASAP={fulfillmentTimeOrASAP}
            changeFulfillment={changeFulfillment}
            requestedFulfillmentIsAsap={requested_fulfillment_is_asap}
            fulfillmentDate={fulfillment_date}
            fulfillmentTime={fulfillment_time}
            currentPrepTime={currentPrepTime}
            desktopAlign={LEFT}
            fulfillmentMethod={fulfillmentMethod}
            dataTestId="cart-fulfillment-method"
            restaurantName={restaurantName}
          />
        )}
        {showGroupOrderInfo && (
          <CartGroupOrderInfo
            groupOrderSpendingLimit={groupOrderSpendingLimit}
            groupOrderDeadline={groupOrderDeadline}
            isGroupOrderAdminDiner={isGroupOrderAdminDiner}
          />
        )}
        {isGroupOrderAdminDiner ? (
          <GroupOrderAdminCartView
            getEditCartItemPath={getEditCartItemPath}
            removeItemFromCart={removeItemFromCart}
            handleEditClick={handleEditClick}
            currentCartPath={currentCartPath}
            adminDiner={adminDiner}
            guestDiners={guestDiners}
          />
        ) : (
          <>
            <Box position="relative">
              <CartBorder />
            </Box>
            <CartItemsList
              items={items}
              getEditCartItemPath={getEditCartItemPath}
              removeItemFromCart={removeItemFromCart}
              handleClose={handleEditClick}
              currentCartPath={currentCartPath}
              showCartQuantity
            />
          </>
        )}
        <CartInfoManager
          toggleIsOpen={toggleIsOpen}
          fulfillmentMethod={fulfillmentMethod}
          total_owed={total_owed}
          tax={tax}
          subtotal={subtotal}
          onlineOrderingServiceFee={onlineOrderingServiceFee}
          taxes_and_fees={taxes_and_fees}
          deliveryFee={deliveryFee}
          customFee={customFee}
          customFeeName={customFeeName}
          bundleServiceFeeWithCustomFee={bundleServiceFeeWithCustomFee}
          bundledServiceAndCustomFees={bundledServiceAndCustomFees}
          digitalGiftCardAmountApplied={digitalGiftCardAmountApplied}
          covidFee={covidFee}
          tipAmount={tipAmount}
          promotion={promotion}
          renderShowAllGuestsButton={renderShowAllGuestsButton}
          dinersCount={dinersCount}
          hideShowAllGuestsButton={hideShowAllGuestsButton}
        />
        {isTotalBelowMinimum && (
          <Box mb={2.5}>
            <Typography variant="caption" color="error.main">
              {`Add $${amountFromMinimum} to reach the minimum of $${orderMinimum}.`}
            </Typography>
          </Box>
        )}
      </StyledCart>
      {!hideUpsells && (
        <StyledUpsellContainer isMobile={isMobile}>
          <MenuUpsell />
        </StyledUpsellContainer>
      )}
      {!isMobile ? (
        <>
          <CheckoutCtaAndErrorsWrapper ref={bottomNavComponentRef}>
            {isGroupOrderGuestDiner ? (
              <GroupOrderDinerFooter />
            ) : (
              <CartPageCheckoutContainer
                toggleIsOpen={toggleIsOpen}
                disableCheckoutButton={disableCheckoutButton}
              />
            )}
            {cartValidationError && renderCartValidationErrors()}
            {cartQuantityError && renderCartQuantityUpdateErrors()}
          </CheckoutCtaAndErrorsWrapper>
        </>
      ) : (
        <>
          <Box>{cartValidationError && renderCartValidationErrors()}</Box>
          <Box>{cartQuantityError && renderCartQuantityUpdateErrors()}</Box>
        </>
      )}
    </>
  );
}

Cart.propTypes = {
  items: PropTypes.array,
  toggleIsOpen: PropTypes.func,
  getEditCartItemPath: PropTypes.func,
  removeItemFromCart: PropTypes.func,
  fulfillmentMethod: PropTypes.string,
  currentPrepTime: PropTypes.number,
  // this will need to be an object eventually
  // to get the location name
  restaurantName: PropTypes.string,
  // to do: make sure this corresponds to fulfillment date time
  requested_fulfillment_is_asap: PropTypes.bool,
  total_owed: PropTypes.number.isRequired,
  tax: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  subtotal: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fulfillment_date: PropTypes.string,
  fulfillment_time: PropTypes.string,
  handleClose: PropTypes.func,
  handleEditClick: PropTypes.func,
  currentCartPath: PropTypes.string,
  changeFulfillment: PropTypes.func,
  isTotalBelowMinimum: PropTypes.bool,
  orderMinimum: PropTypes.number,
  online_ordering_service_fee: PropTypes.string,
  taxes_and_fees: PropTypes.string,
  fulfillmentTimeOrASAP: PropTypes.string,
  shipping_street_address_1: PropTypes.string,
  deliveryFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  customFee: PropTypes.string,
  customFeeName: PropTypes.string,
  bundleServiceFeeWithCustomFee: PropTypes.bool,
  bundledServiceAndCustomFees: PropTypes.string,
  cartValidationError: PropTypes.string,
  cartQuantityError: PropTypes.string,
  digitalGiftCardAmountApplied: PropTypes.number.isRequired,
  covidFee: PropTypes.number.isRequired,
  tipAmount: PropTypes.string.isRequired,
  promotion: PROMOTION_PROPTYPES,
  disableCheckoutButton: PropTypes.bool
};

export default Cart;
